import { throttleAndDebounce } from '@helper/helpers'

/**
 * Handle image sizes. By default we render each image in `imgAttributes` (_PHP_) with `<img sizes=1px />`. We need to adjust the value of the `sizes` attribute on resize to make sure we give the browser the chance to decide which image file included in the `srcset` attribute to load.
 * @requires {@link efficientThrottleAndDebounce}
 * @param {HTMLImageElement[]} images All images in the DOM that have multiple sources available
 */
export const handleImageSizes = images => {
  if (!images) throw Error("Missing required argument 'images'")
  if (images && !Array.isArray(images)) {
    throw TypeError(
      `Wrong type given, expected argument 'images' to be of type 'Array' but received '${typeof images}'`
    )
  }

  /** @type {HTMLImageElement[]} Images that need their `sizes` attribute being set. */
  const items = []

  images.forEach(image => {
    /** Remove the attribute `data-sizes` because it was only needed to query the {@link image} */
    image.removeAttribute('data-sizes')

    /** Add {@link image} to {@link items} */
    items.push(image)
  })

  /**
   * Update the attribute `sizes` on each item in {@link items}
   */
  const onUpdate = () => {
    /**
     * Erst messen, dann schreiben. Beides in einer Schleife zu mischen zwingt
     * den Browser pro Bild zu einem erneuten Layout (Lighthouse: "Forced
     * reflow", auf der Startseite ueber 300 ms).
     */
    const widths = items.map(image =>
      parseInt(image.getBoundingClientRect().width, 10)
    )

    items.forEach((image, index) => {
      const imageWidth = widths[index]

      if (imageWidth > 0) {
        /** When selecting the image size, the browser is normally based on the size of the browser window. Instead, we recommend that the browser uses the size in which the image is actually displayed. */
        image.sizes = imageWidth + 'px'
      } else {
        /** In case we do not know the size of the image, we let the browser do its thing. */
        image.sizes = 'auto'
      }
    })
  }

  throttleAndDebounce(window, 'resize', onUpdate, 100, true)
}
